// Overview: org/user/network/client counts, live calls, GPU/VRAM totals, active
// streams, queued tasks. Static rollups from /overview plus the live panels below —
// tool usage, the network graph, the queues, the tasks.
//
// UX18 — the two panels that are ABOUT networks (Tool usage and the graph) are
// scoped. Everyone starts on their own networks; an admin, the only role for whom the
// other answers exist, gets a control for the rest. ONE control for both panels: a
// graph and a bar chart that disagree about which networks are in scope are worse
// than either alone.

import { useMemo, useState } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useAuth } from '../auth/AuthContext';
import { useStore } from '../store/store';
import { StatCard, Loading, ErrorState, Segmented, bytesHuman } from '../components/ui';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { NetworkFlowSection } from '../components/FlowGraph';
import { QueuesPanel } from '../components/QueuesPanel';
import { TasksPanel } from './Tasks';
import {
  DEFAULT_SCOPE,
  NETWORK_SCOPES,
  SCOPE_LABEL,
  SCOPE_TITLE,
  scopeNetworks,
  scopeUsageRows,
  type NetworkScope,
} from '../lib/networkScope';

export function Overview() {
  const ov = useResource(() => api.overview(), []);
  const { isAdmin, me } = useAuth();
  const [scope, setScope] = useState<NetworkScope>(DEFAULT_SCOPE);
  const networks = useResource(() => api.networks(), []);
  // api.clients() is user-scoped server-side, so its ids bound which nodes the
  // current user owns.
  const clients = useResource(() => api.clients(), []);
  const metrics = useStore((s) => s.metrics);

  const liveVram = useMemo(() => {
    // The event-bus metrics store is global (not per-user); scope the live overlay
    // to the current user's own nodes so a non-admin doesn't see VRAM from nodes
    // they don't own. Each metrics entry is keyed by — and carries — its client_id,
    // so the mapping to a client is exact. Admins keep the full-fleet view. For a
    // non-admin whose client set isn't loaded/empty, this yields total=0 and the
    // StatCard falls back to the already-scoped /overview numbers below.
    const ownIds = new Set((clients.data?.items ?? []).map((c) => c.id));
    const list = Object.values(metrics).filter((m) => isAdmin || ownIds.has(m.client_id));
    const free = list.reduce((n, m) => n + m.gpus.reduce((a, g) => a + g.vram_free_mb, 0), 0);
    const total = list.reduce((n, m) => n + m.gpus.reduce((a, g) => a + g.vram_total_mb, 0), 0);
    return { free, total };
  }, [metrics, isAdmin, clients.data]);

  // The networks in scope, and the two shapes the panels want them in.
  const inScope = useMemo(
    () => scopeNetworks(networks.data?.items ?? [], scope, me?.user.id, me?.memberships ?? []),
    [networks.data, scope, me],
  );
  const scopeIds = useMemo(() => new Set(inScope.map((n) => n.id)), [inScope]);
  const scopeSlugs = useMemo(() => new Set(inScope.map((n) => n.slug)), [inScope]);
  // Until the network list has loaded there is nothing to scope BY, and cutting to an
  // empty set would draw an empty graph and call it an answer. Pass "no opinion".
  const ready = !networks.loading && !networks.error;

  if (ov.loading) return <Loading what="Loading overview…" />;
  if (ov.error || !ov.data) return <ErrorState message={ov.error ?? 'no data'} onRetry={ov.reload} />;
  const { counts, resources, activity } = ov.data;

  return (
    <div className="view">
      <div className="view-head">
        <h1>Overview</h1>
        <div className="view-head-actions">
          {/* Admin-only: a member has exactly one answer to "which networks", and a
              control with one option only teaches you that it exists. */}
          {/* A member has exactly one answer to "which networks", so there is no
              control and — UX22 — no note about it either: a line that says "your
              networks" on the page that only ever shows your networks is a label for
              a setting that does not exist. */}
          {isAdmin && (
            <Segmented<NetworkScope>
              label="Networks"
              value={scope}
              onChange={setScope}
              options={NETWORK_SCOPES.map((v) => ({
                value: v,
                label: <span title={SCOPE_TITLE[v]}>{SCOPE_LABEL[v]}</span>,
              }))}
            />
          )}
        </div>
      </div>

      {/* UX22 — every card that counts something goes to the section that owns it.
          A rollup answers "how many" and then leaves you to find the page yourself;
          the number IS the way in. VRAM free is the one card with no section of its
          own — the per-node figures live on the node cards — so it goes to Nodes,
          which is where those cards are. */}
      <div className="stat-grid">
        <StatCard label="Networks" value={counts.networks} to="/networks" />
        <StatCard label="Nodes online" value={`${counts.clients_online}/${counts.clients}`} to="/clients" />
        <StatCard label="Live providers" value={resources.live_providers} to="/services" title="Open Services" />
        <StatCard
          label="Publications"
          value={counts.publications_approved}
          sub={`${counts.publications} total`}
          to="/services"
          title="Open Services"
        />
        <StatCard
          label="Service defs"
          value={counts.service_definitions}
          sub={`${counts.tools} tools`}
          to="/services"
        />
        <StatCard
          label="In flight"
          value={resources.in_flight}
          sub={`${activity.queued_tasks} queued`}
          to="/#tasks"
          title="Jump to the Tasks panel below"
        />
        <StatCard label="Active streams" value={activity.active_streams} to="/streams" />
        <StatCard
          label="VRAM free"
          value={bytesHuman(((liveVram.total ? liveVram.free : resources.vram_free_mb)) * 1024 * 1024)}
          sub={`of ${bytesHuman(((liveVram.total || resources.vram_total_mb)) * 1024 * 1024)}`}
          to="/clients"
          title="Open Nodes — VRAM is reported per node"
        />
      </div>

      {/* Tool usage — the caller's own per-tool aggregate (server-scoped: a normal user
          sees only their own usage, incl. editor task-token-originated calls now attributed
          to them; an admin sees org-wide), narrowed further to the networks in scope
          (UX18). Top tools by call count as inline bars + totals. */}
      <CollapsibleSection title="Tool usage" storageKey="overview.collapsed:tool-usage">
        <ToolUsage slugs={ready ? scopeSlugs : null} />
      </CollapsibleSection>

      {/* H36 — Network graph now comes BEFORE Activity (the two blocks were swapped).
          H42 — both are CollapsibleSection: clicking the title toggles the block (no
          separate button); the collapsed state persists in localStorage.
          The network-flow graph: Networks → Clients×Network → Services → Tools, with
          per-node task-queue figures + drill-in (H37/H38). */}
      <CollapsibleSection title="Network graph" storageKey="overview.collapsed:network-graph">
        <NetworkFlowSection networkIds={ready ? scopeIds : undefined} />
      </CollapsibleSection>

      {/* UX6 — the task queues sit DIRECTLY under the graph, because "the fleet is
          behind" and "which node is behind" are one question asked twice. They used
          to be a tab inside the Activity block. */}
      <CollapsibleSection title="Queues" storageKey="overview.collapsed:queues">
        <QueuesPanel />
      </CollapsibleSection>

      {/* UX19 — Tasks is a PANEL here, and only here: it is not a nav row any more.
          What is running belongs on the page that says how things are going, under the
          queues that explain why it is waiting. /tasks redirects to this page.
          It takes the SAME scope as the graph and the usage bars (§18): a member has one
          answer and no control, and the gateway already hands them only the tasks of the
          networks they belong to; an admin moves the control and the table moves with it,
          instead of being the one block on the page still showing the whole org.
          OW1 — a member's panel carries its own «Мои» / «Сети» control (own calls in any
          network of the org vs. the member-network ledger); see TasksPanel. */}
      <div id="tasks">
        <CollapsibleSection title="Tasks" storageKey="overview.collapsed:tasks">
          <TasksPanel networkIds={ready ? scopeIds : undefined} />
        </CollapsibleSection>
      </div>
    </div>
  );
}

// Per-tool usage for the signed-in user (GET /usage/by-tool). The endpoint already scopes
// to the caller, so there's no user filtering here — just render the top tools by call
// count as horizontal bars (consistent with the .progress bar style) plus the totals.
const TOP_N = 10;
function ToolUsage({ slugs }: { slugs: Set<string> | null }) {
  const res = useResource(() => api.usageByTool(), []);
  if (res.loading) return <Loading />;
  if (res.error || !res.data) return <ErrorState message={res.error ?? 'no data'} onRetry={res.reload} />;
  // UX18 — a canonical tool names its network first, so the scope is a prefix match.
  const rows = scopeUsageRows(res.data.items, slugs);
  if (rows.length === 0) {
    return (
      <div className="muted small">
        {res.data.items.length === 0
          ? 'No tool usage yet.'
          : 'No tool usage in the networks in scope.'}
      </div>
    );
  }

  const top = rows.slice(0, TOP_N);
  const max = Math.max(...top.map((r) => r.calls), 1);
  const totals = rows.reduce(
    (a, r) => ({ calls: a.calls + r.calls, gpu_seconds: a.gpu_seconds + r.gpu_seconds, tokens: a.tokens + r.tokens }),
    { calls: 0, gpu_seconds: 0, tokens: 0 },
  );

  return (
    <>
      <div className="muted-row">
        <span>Σ {totals.calls} calls</span>
        <span>{totals.gpu_seconds.toFixed(1)} GPU·s</span>
        <span>{totals.tokens} tokens</span>
      </div>
      <ul className="usage-bars">
        {top.map((r) => (
          <li key={r.tool} className="usage-bar-row">
            <code className="mono small usage-bar-label" title={r.tool}>{r.tool}</code>
            <div className="progress usage-bar-track">
              <div className="progress-fill" style={{ width: `${(r.calls / max) * 100}%` }} />
            </div>
            <span className="usage-bar-value">{r.calls}</span>
          </li>
        ))}
      </ul>
    </>
  );
}
